import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Download, Share2, Loader2 } from 'lucide-react';
import { t } from '../../lib/i18n';
import { buildBeforeAfterImage, downloadBlob, beforeAfterFilename } from '../../lib/beforeAfterImage';

const canShareFiles = (file) => {
  try {
    return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
  } catch (e) {
    return false;
  }
};

/**
 * Download + native share of a composed Before/After PNG.
 * The composite is pre-built as soon as the sources are known so the share
 * call happens right inside the user gesture (required by iOS Safari).
 *
 * Optional props let the same component serve the progress comparison:
 *  - labels: [left, right] panel labels (default AVANT / APRES)
 *  - subtitle: footer line under the PrimeFace brand
 *  - filename: output file name (default primeface-avant-apres-<date>.png)
 *  - shareTitle / shareText: Web Share metadata
 *  - downloadLabel / shareLabel: button captions
 *  - testIdPrefix: prefix for data-testid (default "simulation")
 */
export const ShareActions = ({
  beforeSrc,
  afterSrc,
  language,
  labels = null,
  subtitle = null,
  filename = null,
  shareTitle = null,
  shareText = null,
  downloadLabel = null,
  shareLabel = null,
  testIdPrefix = 'simulation',
  className = 'mt-4',
}) => {
  const [busy, setBusy] = useState(null); // 'download' | 'share' | null
  const blobRef = useRef(null);
  const buildingRef = useRef(null);
  const labelsKey = labels ? labels.join('|') : '';

  useEffect(() => {
    blobRef.current = null;
    if (!beforeSrc || !afterSrc) return undefined;
    let cancelled = false;
    buildingRef.current = buildBeforeAfterImage({ beforeSrc, afterSrc, language, labels, subtitle })
      .then(blob => { if (!cancelled) blobRef.current = blob; return blob; })
      .catch(() => null);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beforeSrc, afterSrc, language, labelsKey, subtitle]);

  const getBlob = async () => {
    if (blobRef.current) return blobRef.current;
    const built = buildingRef.current ? await buildingRef.current : null;
    if (built) return built;
    const blob = await buildBeforeAfterImage({ beforeSrc, afterSrc, language, labels, subtitle });
    blobRef.current = blob;
    return blob;
  };

  const outputName = filename || beforeAfterFilename(language);

  const handleDownload = async () => {
    if (busy) return;
    setBusy('download');
    try {
      downloadBlob(await getBlob(), outputName);
      toast.success(t('imageDownloaded', language));
    } catch (err) {
      toast.error(t('imageDownloadError', language));
    } finally {
      setBusy(null);
    }
  };

  const handleShare = async () => {
    if (busy) return;
    setBusy('share');
    try {
      const blob = await getBlob();
      const file = new File([blob], outputName, { type: 'image/png' });
      if (!canShareFiles(file)) {
        downloadBlob(blob, file.name);
        toast.info(t('shareUnavailable', language));
        return;
      }
      await navigator.share({
        files: [file],
        title: shareTitle || t('shareTitle', language),
        text: shareText || t('shareText', language),
      });
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error(t('shareError', language));
    } finally {
      setBusy(null);
    }
  };

  const disabled = Boolean(busy) || !beforeSrc || !afterSrc;
  const outline = 'bg-[var(--lux-panel)] border-lux-gold/40 text-white hover:bg-lux-gold/10 hover:text-[var(--lux-gold)] hover:border-[var(--lux-gold)] active:scale-[0.98] transition-[background-color,transform,border-color] duration-200';

  return (
    <div className={`flex flex-wrap justify-center gap-3 ${className}`} data-testid={`${testIdPrefix}-share-actions`}>
      <Button data-testid={`${testIdPrefix}-download-image-button`} onClick={handleDownload} disabled={disabled} variant="outline" className={outline}>
        {busy === 'download' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2 text-[var(--lux-gold)]" />}
        {busy === 'download' ? t('downloadingImage', language) : (downloadLabel || t('downloadImage', language))}
      </Button>
      <Button data-testid={`${testIdPrefix}-share-button`} onClick={handleShare} disabled={disabled} className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200">
        {busy === 'share' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
        {busy === 'share' ? t('sharingImage', language) : (shareLabel || t('shareImage', language))}
      </Button>
    </div>
  );
};
